import React, { useEffect, useMemo, useState } from "react";
import { GoogleMap, Marker } from "@react-google-maps/api";
import * as R from "ramda";
import {
  Icon,
  Collapse as AntdCollapse,
  Col,
  Tag,
  Popover,
  Progress
} from "antd";
import { compose } from "recompose";
import { withTranslation } from "react-i18next";
import { connect, useSelector } from "react-redux";
import moment from "moment";
import { jsPDF } from "jspdf";
import { Tags } from "@worcket/common/components/listing";

import * as gtag from "../lib/gtag";
import { getLanguage } from "../utils/data/static/languages";
import {
  FETCH_PROFESSIONS,
  FETCH_SKILLS,
  FETCH_STUDIES
} from "../store/actions/catalogs";
import { ACCEPT_LANGUAGE } from "../locales/acceptedLanguages";
import { getCountry } from "../utils/data/static/countries";
import { formatAddress } from "../utils/candidates.js";

import CandidateVideo from "./CandidateVideo";
import Button from "./Button";
import CandidateTags from "./CandidateTags/CandidateTags";
import { EquifaxTag } from "./EquifaxTag";

import styled from "styled-components/macro";

const Collapse = styled(AntdCollapse)`
  .ant-tag {
    margin: 0 4px 0 0;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .ant-collapse-item {
    &:not(:last-child) {
      border-bottom: 1px solid #e8e8e8;
    }

    &:last-child {
      border-bottom: 0px;
    }
  }

  @media (min-width: 768px) and (max-width: 1024px) {
    background: #ffffff;
    padding: 18px 22px 32px 18px;

    .ant-tag {
      border-radius: 14px;
      line-height: 22px;
      margin: 0 8px 8px 0;
      padding: 0 12px;
    }

    .ant-tag.good {
      border-radius: 15px 0px 0px 15px;
      height: 26px;
    }

    .ant-tag.info {
      border-radius: 0px 15px 15px 0px;
      height: 26px;
    }

    .ant-collapse-item {
      background: #ffffff;
      overflow: hidden;
    }

    .ant-collapse-header {
      align-items: center;
      border-radius: 10px 10px 0 0 !important;
      min-height: 56px;
    }

    .ant-collapse-content {
      border-top: 1px solid #edf1f7;
    }

    .ant-collapse-content-box {
      padding: 20px 24px 24px;
    }

    .ant-row {
      align-items: stretch !important;
      display: flex;
      flex-wrap: wrap;
      gap: 18px 0;
      margin: 0 !important;
    }

    .ant-col-6,
    .ant-col-9,
    .ant-col-12 {
      min-width: 0;
      padding: 0 18px;
    }

    .ant-col-6:not(:first-child),
    .ant-col-8:not(:first-child),
    .ant-col-9:not(:first-child),
    .ant-col-12:not(:first-child) {
      border-left: 1px solid #edf1f7;
    }
  }
`;

export const Subtitle = styled.span`
  display: inline-block;
  font-size: 14px;
  font-weight: 600;
  color: #343a40;
`;

export const Panel = styled(AntdCollapse.Panel)`
  background: #ffffff;

  .ant-collapse-header {
    border-radius: 0 !important;
    user-select: none;
  }
`;

const Row = styled.div`
  font-size: 14px;
  line-height: 20px;
  display: flex;
  align-items: center;
  margin: 10px 0;
  .anticon {
    margin-right: 8px;
  }
`;

const Title = styled.span`
  font-size: 15px;
  font-weight: bold;
  color: black;
`;

const DetailsItem = styled.span`
  color: #747c84;
  font-size: 14px;
`;

const ContentPanel = styled.div`
  margin-left: 23px;
  white-space: pre-wrap;

  @media (min-width: 768px) and (max-width: 1024px) {
    margin-left: 0;
  }
`;

const TitleInformation = styled.p`
  color: #343a40;
  font-weight: 600;
  margin-bottom: 0.5em;

  @media (min-width: 768px) and (max-width: 1024px) {
    margin-bottom: 6px;
  }

  .ant-btn-round.ant-btn-sm {
    height: 18px;
    padding: 0 12px;
    font-size: 11px;
    border-radius: 26px;
    margin-left: 10px;
  }
`;

const ItemInformation = styled.p`
  color: #747c84;
  margin-bottom: 5px;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (min-width: 768px) and (max-width: 1024px) {
    white-space: normal;
  }
  a {
    color: #3d64ff;
  }
`;

const LastItemInformation = styled.p`
  color: #747c84;
`;

const OperationNumberInformation = styled(LastItemInformation)`
  max-width: 100%;
  overflow-wrap: anywhere;
  word-break: break-word;
  white-space: normal;
`;

const AddressInformation = styled.p`
  color: #747c84;
  margin-bottom: 5px;
`;

const PositionItem = styled(Row)`
  padding-bottom: 10px;
  align-items: baseline;

  &:not(:last-child) {
    border-bottom: 1px solid #e8e8e8;
  }

  &:last-child {
    border-bottom: 0px;
  }
`;

const IndustriesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-top: 5px;
`;

const Telephone = styled.span`
  color: #747c84;
`;

const NationalIdType = styled.p`
  color: #747c84;
  margin-bottom: 0px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const NationalIdValue = styled.p`
  color: #747c84;
`;

const ReferralContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;

  .referral-name {
    font-weight: bold;
    margin-bottom: 6px;
  }
`;

const Images = styled.div`
  display: flex;
  column-gap: 1.5em;
  height: 165px;

  .item {
    flex-basis: 0;
    flex-grow: 1;
  }

  .photo {
    background-color: #f7f9fa;
    height: calc(100% - 25px);
    border: solid #e5e9ec;
  }
`;

const EquifaxHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  @media (max-width: 1024px) {
    flex-wrap: 7px;
    flex-direction: column;
    align-items: flex-start;
    gap: 7px;
  }
`;
const EquifaxHeaderActions = styled.div`
  display: flex;
  align-items: center;
  column-gap: 24px;

  @media (max-width: 767px) {
    flex-wrap: wrap-reverse;
    gap: 7px;
  }
`;

const tagsStyle = {
  verticalAlign: "middle",
  marginBottom: 5,
  borderRadius: 15,
  borderColor: "#e5e9ec",
  color: "#747d84",
  background: "white",
  paddingLeft: 10,
  paddingRight: 10,
  marginRight: 7
};

// Normaliza la decisión de Equifax leyendo distintos formatos de respuesta (legacy y actual).
// Prioriza el formato antiguo por compatibilidad. Si no hay valor, retorna string vacío.
const getEquifaxDecision = response => {
  const legacyDecision = R.pathOr(
    "",
    ["grupoCampos", "campo", 0, "valor"],
    response
  );
  if (legacyDecision) return legacyDecision;

  const groups = R.pathOr([], ["grupoCampos"], response);
  if (!Array.isArray(groups)) return "";

  return (
    R.pathOr("", [0, "valor"], groups) ||
    R.pathOr("", [0, "campo", 0, "valor"], groups)
  );
};

// Normaliza el número de operación de Equifax leyendo distintos formatos de respuesta (legacy y actual).
const getEquifaxOperationNumber = response =>
  R.pathOr("", ["header", "operationNumber"], response) ||
  R.pathOr("", ["header", "numeroOperacion"], response) ||
  R.pathOr("", ["header", "nroOperacion"], response) ||
  R.pathOr("", ["operationNumber"], response) ||
  R.pathOr("", ["numeroOperacion"], response) ||
  R.pathOr("", ["nroOperacion"], response) ||
  R.pathOr("", ["transactionId"], response) ||
  R.pathOr("", ["transactionID"], response);

const loadImageAsDataUrl = src =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";

    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = image.width;
      canvas.height = image.height;

      const context = canvas.getContext("2d");
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0);

      resolve(canvas.toDataURL("image/jpeg", 0.95));
    };

    image.onerror = reject;
    image.src = src;
  });

// Genera un PDF con la información de Equifax y lo descarga.
const downloadEquifaxPdf = async (equifaxInfo, t) => {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const logo = await loadImageAsDataUrl("/equifax-logo.png");

  const status = t(
    `opportunity:process.candidateDetails.equifaxInfo.${
      equifaxInfo.decision ? "proceed" : "rejected"
    }`
  );

  doc.addImage(logo, "JPEG", 72, 70, 210, 60);
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(12);
  doc.text(moment(equifaxInfo.date).format("DD [de] MMMM [de] YYYY"), 420, 90);

  doc.setFontSize(18);
  doc.text(status, 420, 120);

  doc.setFontSize(22);
  doc.text("Informe", 297, 190, { align: "center" });

  const rows = [
    ["Nombres:", equifaxInfo.nombres],
    ["Apellido paterno:", equifaxInfo.apellidoPaterno],
    ["Apellido materno:", equifaxInfo.apellidoMaterno],
    [
      "Tipo de documento:",
      t(
        `opportunity:process.candidateDetails.personalInfoTab.typesDNI.${equifaxInfo.tipoDocumento}`
      )
    ],
    ["Número de documento:", equifaxInfo.numeroDocumento],
    ["Fecha de verificación:", moment(equifaxInfo.date).format("DD-MM-YYYY")],
    ...(equifaxInfo.operationNumber
      ? [["Número de operación:", equifaxInfo.operationNumber]]
      : [])
  ];

  let y = 260;
  rows.forEach(([label, value]) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text(label, 150, y);

    doc.setFont("helvetica", "normal");
    doc.text(String(value || "-"), 330, y); // Convertir a string para evitar errores si value es null o undefined

    y += 38;
  });

  doc.save(`equifax-${equifaxInfo.numeroDocumento || "informe"}.pdf`);
};

const CandidateDetails = ({
  t,
  i18n,
  id,
  opportunityId,
  companyTags = [],
  tags = [],
  bio,
  email,
  phoneNumber,
  country,
  province = {},
  district = {},
  address,
  answers,
  typeNationalID,
  nationalId,
  othersNationalId = [],
  birthDate,
  experience,
  skills = [],
  educationLevel,
  languages = [],
  professions = [],
  universities = [],
  studyProgress,
  graduationYear,
  degrees = [],
  fieldsOfStudy = [],
  video,
  videoMetadata,
  lastPositions = [],
  isRegistered,
  tests = []
}) => {
  const locale = ACCEPT_LANGUAGE[i18n.language] || ACCEPT_LANGUAGE.DEFAULT;
  moment.locale(locale);

  const hasNationalId =
    !R.isNil(typeNationalID) && !R.isEmpty(String(nationalId || "").trim());
  const visibleOtherNationalIds = othersNationalId.filter(
    ({ type, value }) =>
      !R.isNil(type) && !R.isEmpty(String(value || "").trim())
  );

  useEffect(() => {
    gtag.event({
      category: "Candidate",
      action: `Candidate-${isRegistered ? "Registered" : "External"}.viewDetail`
    });
  }, [isRegistered]);

  const provinceName = province?.name;
  const districtName = district?.name;
  const addressInfo = useMemo(
    () =>
      formatAddress({
        country: getCountry(country),
        province: provinceName,
        district: districtName
      }),
    [country, provinceName, districtName]
  );

  const currentPosition = useMemo(
    () => R.find(R.prop("current"))(lastPositions),
    [lastPositions]
  );

  const lastPosition = useMemo(
    () =>
      R.pipe(
        R.sortBy(R.prop("end")),
        R.last
      )(lastPositions),
    [lastPositions]
  );

  const positionsSorted = useMemo(
    () =>
      R.sortWith([
        R.descend(R.propEq("current", true)),
        R.descend(R.prop("end"))
      ])(lastPositions),
    [lastPositions]
  );

  const identityVerificationEnabled = useSelector(state => {
    let res = R.pathOr(
      undefined,
      ["company", "services", "identityVerification"],
      state
    );
    if (res === undefined) {
      res = R.pathOr(
        false,
        ["headquarter", "services", "identityVerification"],
        state
      );
    }
    return res;
  });

  const equifaxVerificationEnabled = useSelector(state => {
    let res = R.pathOr(undefined, ["company", "services", "equifax"], state);
    if (res === undefined) {
      res = R.pathOr(false, ["headquarter", "services", "equifax"], state);
    }
    return res;
  });

  const equifaxInfo = useMemo(() => {
    if (!equifaxVerificationEnabled) return;
    const test = tests.find(
      t =>
        t &&
        t.source === "worcket" &&
        t.testIdentifier === "7" &&
        t.serviceCallResult === "success"
    );
    const res = test && test.serviceCallResultData;
    if (!res) return;
    const decision = getEquifaxDecision(res);
    const integrante = R.pathOr({}, ["integrante"], res);

    return {
      ...integrante,
      tipoDocumento: integrante.tipoDocumento || typeNationalID,
      operationNumber: getEquifaxOperationNumber(res),
      decision: decision === "PROCEDE",
      date: test.date
    };
  }, [equifaxVerificationEnabled, tests, typeNationalID]);
  const facenoteInfo = useMemo(() => {
    if (!identityVerificationEnabled) return;
    const test = tests.find(
      t =>
        t &&
        t.source === "worcket" &&
        t.testIdentifier === "4" &&
        t.serviceCallResult === "success"
    );
    const res = test && test.serviceCallRequest;
    if (!res) return;
    return {
      ...R.pathOr({}, ["id", "idData", "parsedInfo"], res),
      ...R.pathOr({}, ["id", "idData", "images"], res),
      mrzFound: R.pathOr(false, ["id", "idData", "mrzFound"], res),
      faceMatch: R.pathOr("", ["id", "idData", "faceMatch"], res),
      faceMatchPercentage: R.clamp(
        0,
        100,
        R.pathOr(0, ["id", "idData", "faceMatchPercentage"], res)
      ),
      selfie: R.pathOr("", ["selfieImage"], res)
    };
  }, [identityVerificationEnabled, tests]);

  const progressColors = R.cond([
    [R.gte(40), R.always("#F5242E")],
    [R.gte(60), R.always("#e0b500")],
    [R.T, R.always("#7DCB13")]
  ]);

  const showPersonalInfo =
    email || phoneNumber || R.prop("name", address) || birthDate;
  const courses = Boolean(degrees.length) || Boolean(universities.length);
  const fields = Boolean(languages.length) || Boolean(fieldsOfStudy.length);

  const showEducation = educationLevel || courses || fields;

  const referralAnswer = useMemo(() => {
    return R.pipe(
      R.defaultTo([]),
      R.find(R.propEq("type", "referral")),
      R.defaultTo({}),
      R.prop("answer")
    )(answers);
  }, [answers]);

  const educationLvl = !educationLevel
    ? null
    : `${t(`catalogs:educationLevelsAdvanced.${educationLevel}`)} ${
        educationLevel.match("-avanzado")
          ? (typeof studyProgress !== "undefined" &&
              studyProgress !== null &&
              `(${t(
                "opportunity:process.candidateDetails.studyProgress"
              ).toLowerCase()} ${studyProgress}%)`) ||
            ""
          : (graduationYear &&
              `(${t(
                "opportunity:process.candidateDetails.graduatedIn"
              ).toLowerCase()} ${moment(graduationYear).format(
                "MMMM, YYYY"
              )})`) ||
            ""
      }`;

  const showProfessionalInfo =
    R.type(experience) === "Number" ||
    Boolean(professions.length) ||
    Boolean(skills.length);

  const showTags = !R.isNil(opportunityId);
  const [selectedTags, setSelectedTags] = useState(
    R.pathOr([], [opportunityId], tags)
  );

  return (
    <Collapse
      defaultActiveKey={[
        t("opportunity:process.candidateDetails.referral"),
        t("opportunity:process.candidateDetails.equifaxInfo.title"),
        t("opportunity:process.candidateDetails.facenoteInfo.title"),
        t("opportunity:process.candidateDetails.personalInfo"),
        // t("opportunity:process.candidateDetails.professionalExp"),
        // t("opportunity:process.candidateDetails.lastPositions"),
        t("opportunity:process.candidateDetails.tags")
      ]}
      bordered={false}
      expandIcon={({ isActive }) => (
        <Icon
          type="right"
          style={{ fontSize: "16px", color: "#3D64FF" }}
          theme="outlined"
          rotate={isActive ? 90 : 0}
        />
      )}
    >
      {referralAnswer && referralAnswer.wasReferred && (
        <Panel
          header={
            <Title>{t("opportunity:process.candidateDetails.referral")}</Title>
          }
          key={t("opportunity:process.candidateDetails.referral")}
        >
          <ContentPanel>
            <ReferralContainer>
              <span className="referral-name">{referralAnswer.name}</span>
              <a href={`mailto:${referralAnswer.email}`}>
                <Icon type="mail" />
                {` ${referralAnswer.email}`}
              </a>
            </ReferralContainer>
          </ContentPanel>
        </Panel>
      )}
      {equifaxInfo && (
        <Panel
          header={
            <EquifaxHeader>
              <Title>
                {t("opportunity:process.candidateDetails.equifaxInfo.title")}
              </Title>
              <EquifaxHeaderActions>
                <Button
                  type="tertiary"
                  size="lg"
                  icon="download"
                  onClick={event => {
                    event.stopPropagation(); // Evita que se colapse el panel al hacer click en el botón
                    downloadEquifaxPdf(equifaxInfo, t);
                  }}
                >
                  Descargar PDF
                </Button>
                <EquifaxTag proceed={equifaxInfo.decision} />
              </EquifaxHeaderActions>
            </EquifaxHeader>
          }
          key={t("opportunity:process.candidateDetails.equifaxInfo.title")}
        >
          <ContentPanel>
            <Row style={{ alignItems: "baseline" }}>
              <Col span={9}>
                <TitleInformation>
                  {t("opportunity:process.candidateDetails.equifaxInfo.names")}
                </TitleInformation>
                <LastItemInformation>{equifaxInfo.nombres}</LastItemInformation>
                <TitleInformation>
                  {t(
                    "opportunity:process.candidateDetails.equifaxInfo.dniType"
                  )}
                </TitleInformation>
                <LastItemInformation>
                  {t(
                    `opportunity:process.candidateDetails.personalInfoTab.typesDNI.${equifaxInfo.tipoDocumento}`
                  )}
                </LastItemInformation>
                <TitleInformation>
                  {t(
                    "opportunity:process.candidateDetails.equifaxInfo.verificationDate"
                  )}
                </TitleInformation>
                <LastItemInformation>
                  {moment(equifaxInfo.date).format(
                    t(
                      "opportunity:process.candidateDetails.equifaxInfo.verificationDateFormat"
                    )
                  )}
                </LastItemInformation>
              </Col>
              <Col span={9}>
                <TitleInformation>
                  {t(
                    "opportunity:process.candidateDetails.equifaxInfo.paternalSurname"
                  )}
                </TitleInformation>
                <LastItemInformation>
                  {equifaxInfo.apellidoPaterno}
                </LastItemInformation>
                <TitleInformation>
                  {t("opportunity:process.candidateDetails.equifaxInfo.dni")}
                </TitleInformation>
                <LastItemInformation>
                  {equifaxInfo.numeroDocumento}
                </LastItemInformation>
              </Col>
              <Col span={9}>
                <TitleInformation>
                  {t(
                    "opportunity:process.candidateDetails.equifaxInfo.maternalSurname"
                  )}
                </TitleInformation>
                <LastItemInformation>
                  {equifaxInfo.apellidoMaterno}
                </LastItemInformation>
                <TitleInformation>
                  {t(
                    "opportunity:process.candidateDetails.equifaxInfo.operationNumber"
                  )}
                </TitleInformation>
                <OperationNumberInformation>
                  {equifaxInfo.operationNumber}
                </OperationNumberInformation>
              </Col>
            </Row>
          </ContentPanel>
        </Panel>
      )}
      {facenoteInfo && (
        <Panel
          header={
            <Title>
              {t("opportunity:process.candidateDetails.facenoteInfo.title")}
            </Title>
          }
          key={t("opportunity:process.candidateDetails.facenoteInfo.title")}
        >
          <ContentPanel>
            <Row style={{ alignItems: "baseline" }}>
              <Col span={9}>
                <TitleInformation>
                  {t("opportunity:process.candidateDetails.facenoteInfo.dni")}
                </TitleInformation>
                <LastItemInformation>{facenoteInfo.number}</LastItemInformation>
                <TitleInformation>
                  {t("opportunity:process.candidateDetails.facenoteInfo.name")}
                </TitleInformation>
                <LastItemInformation>
                  {facenoteInfo.name || facenoteInfo.fullName}
                </LastItemInformation>
                <TitleInformation>
                  {t(
                    "opportunity:process.candidateDetails.facenoteInfo.birthDate"
                  )}
                </TitleInformation>
                <LastItemInformation>
                  {moment(facenoteInfo.birthDate, "YY-MM-DD").format(
                    t("opportunity:process.candidateDetails.birthDateFormat")
                  )}
                </LastItemInformation>
              </Col>
              <Col span={9}>
                <TitleInformation>
                  {t(
                    "opportunity:process.candidateDetails.facenoteInfo.issuingCountry"
                  )}
                </TitleInformation>
                <LastItemInformation>
                  {facenoteInfo.issuingCountry}
                </LastItemInformation>
                <TitleInformation>
                  {t(
                    "opportunity:process.candidateDetails.facenoteInfo.gender"
                  )}
                </TitleInformation>
                <LastItemInformation>{facenoteInfo.gender}</LastItemInformation>
                <TitleInformation>
                  {t(
                    "opportunity:process.candidateDetails.facenoteInfo.expirationDate"
                  )}
                </TitleInformation>
                <LastItemInformation>
                  {moment(facenoteInfo.expirationDate, "YY-MM-DD").format(
                    t("opportunity:process.candidateDetails.birthDateFormat")
                  )}
                </LastItemInformation>
              </Col>
              <Col span={9}>
                <TitleInformation>&nbsp;</TitleInformation>
                <LastItemInformation>&nbsp;</LastItemInformation>
                <TitleInformation>
                  {t(
                    "opportunity:process.candidateDetails.facenoteInfo.nationality"
                  )}
                </TitleInformation>
                <LastItemInformation>
                  {facenoteInfo.nationality}
                </LastItemInformation>
                <TitleInformation>
                  {t(
                    "opportunity:process.candidateDetails.facenoteInfo.verificationMethod"
                  )}
                </TitleInformation>
                <LastItemInformation>
                  {t(
                    `opportunity:process.candidateDetails.facenoteInfo.${
                      facenoteInfo.mrzFound ? "automatic" : "manual"
                    }`
                  )}
                </LastItemInformation>
              </Col>
            </Row>
            <Row
              style={{
                alignItems: "baseline",
                marginTop: "1em"
              }}
            >
              <Col style={{ width: "100%" }}>
                <TitleInformation>
                  {t(
                    "opportunity:process.candidateDetails.facenoteInfo.faceMatch",
                    {
                      name: t(
                        `opportunity:process.candidateDetails.facenoteInfo.${facenoteInfo.faceMatch}`
                      ),
                      num: facenoteInfo.faceMatchPercentage.toLocaleString(
                        undefined,
                        { maximumFractionDigits: 2 }
                      )
                    }
                  )}
                </TitleInformation>
                <Progress
                  showInfo={false}
                  percent={facenoteInfo.faceMatchPercentage}
                  strokeColor={progressColors(facenoteInfo.faceMatchPercentage)}
                />
              </Col>
            </Row>
            <Row
              style={{
                alignItems: "baseline",
                marginTop: "1em"
              }}
            >
              <Col style={{ width: "100%" }}>
                <Images>
                  {Object.entries(
                    R.pick(["front", "back", "face", "selfie"], facenoteInfo)
                  ).map(([key, value]) => (
                    <div className="item">
                      <div>
                        <TitleInformation>
                          {t(
                            `opportunity:process.candidateDetails.facenoteInfo.${key}`
                          )}
                        </TitleInformation>
                      </div>
                      <div className="photo">
                        <img src={value} alt="" />
                      </div>
                    </div>
                  ))}
                </Images>
              </Col>
            </Row>
          </ContentPanel>
        </Panel>
      )}
      {showPersonalInfo && (
        <Panel
          header={
            <Title>
              {t("opportunity:process.candidateDetails.personalInfo")}
            </Title>
          }
          key={t("opportunity:process.candidateDetails.personalInfo")}
        >
          <ContentPanel>
            <Row style={{ alignItems: "baseline" }}>
              <Col span={9}>
                {(hasNationalId || visibleOtherNationalIds.length > 0) && (
                  <>
                    <TitleInformation>
                      {t(
                        "opportunity:process.candidateDetails.personalInfoTab.dni"
                      )}
                    </TitleInformation>
                    {hasNationalId && (
                      <>
                        <NationalIdType>
                          {t(
                            `opportunity:process.candidateDetails.personalInfoTab.typesDNI.${typeNationalID}`
                          )}
                        </NationalIdType>
                        <NationalIdValue>{nationalId}</NationalIdValue>
                      </>
                    )}
                    {visibleOtherNationalIds.map(({ type, value }) => (
                      <React.Fragment key={`${type}-${value}`}>
                        <NationalIdType>
                          {t(
                            `opportunity:process.candidateDetails.personalInfoTab.typesDNI.${type}`
                          )}
                        </NationalIdType>
                        <NationalIdValue>{value}</NationalIdValue>
                      </React.Fragment>
                    ))}
                  </>
                )}
                <TitleInformation>
                  {t(
                    "opportunity:process.candidateDetails.personalInfoTab.titleContact"
                  )}
                </TitleInformation>
                <ItemInformation>
                  <Icon type="mail" />
                  {email ? (
                    <a href={`mailto:${email}`}>
                      <b>{email}</b>
                    </a>
                  ) : (
                    "-"
                  )}
                </ItemInformation>
                <ItemInformation>
                  <Icon type="phone" rotate="90" />
                  <Telephone>{phoneNumber ? phoneNumber : "-"}</Telephone>
                </ItemInformation>
              </Col>
              {birthDate && (
                <Col span={6}>
                  <TitleInformation>
                    {t(
                      "opportunity:process.candidateDetails.personalInfoTab.titleBirth"
                    )}
                  </TitleInformation>
                  <ItemInformation>
                    {moment(birthDate).format(
                      t("opportunity:process.candidateDetails.birthDateFormat")
                    )}
                  </ItemInformation>
                  <ItemInformation>
                    {t(
                      "opportunity:process.candidateDetails.personalInfoTab.years",
                      {
                        years: moment().diff(moment(birthDate), "years")
                      }
                    )}
                  </ItemInformation>
                </Col>
              )}
              {(address || country) && (
                <Col span={9}>
                  <TitleInformation>
                    {t(
                      "opportunity:process.candidateDetails.personalInfoTab.titleAddress"
                    )}
                    {address &&
                      address.location &&
                      address.location.lat &&
                      address.location.lon && (
                        <Popover
                          placement="left"
                          title={
                            R.prop("name", address) ? (
                              <b>{address.name}</b>
                            ) : null
                          }
                          content={
                            <GoogleMap
                              options={{
                                gestureHandling: "cooperative",
                                minZoom: 15,
                                maxZoom: 17,
                                zoomControl: true,
                                controlSize: 25,
                                streetViewControl: false,
                                panControl: false,
                                mapTypeControl: false
                              }}
                              mapContainerStyle={{
                                height: "240px",
                                width: "500px",
                                borderRadius: 5
                              }}
                              clickableIcons={false}
                              zoom={16}
                              center={{
                                lat: address.location.lat,
                                lng: address.location.lon
                              }}
                            >
                              <Marker
                                position={{
                                  lat: address.location.lat,
                                  lng: address.location.lon
                                }}
                              />
                            </GoogleMap>
                          }
                          trigger="click"
                        >
                          <Button
                            style={{ margin: 10 }}
                            type="tertiary"
                            size="xs"
                          >
                            {t("opportunity:process.candidateDetails.viewMap")}
                          </Button>
                        </Popover>
                      )}
                  </TitleInformation>
                  <AddressInformation>
                    <div>{address?.name}</div>
                    <div>{addressInfo}</div>
                  </AddressInformation>
                </Col>
              )}
            </Row>
          </ContentPanel>
        </Panel>
      )}
      {bio && (
        <Panel
          header={
            <Title>{t("opportunity:process.candidateDetails.bio")}</Title>
          }
          key={t("opportunity:process.candidateDetails.bio")}
        >
          <ContentPanel>{bio}</ContentPanel>
        </Panel>
      )}
      {video && (
        <Panel
          header={
            <Title>{t("opportunity:process.candidateDetails.video")}</Title>
          }
          key={t("opportunity:process.candidateDetails.video")}
        >
          <ContentPanel>
            <CandidateVideo
              video={video}
              controls
              autoPlay
              videoMetadata={videoMetadata}
            />
          </ContentPanel>
        </Panel>
      )}
      {showEducation && (
        <Panel
          header={
            <>
              <Title>
                {t("opportunity:process.candidateDetails.education")}
              </Title>
              {educationLvl && (
                <>
                  <Icon
                    type="right"
                    style={{ marginLeft: 5, marginRight: 5, color: "#9ca8b1" }}
                  />
                  <DetailsItem>{educationLvl}</DetailsItem>
                </>
              )}
            </>
          }
          key={t("opportunity:process.candidateDetails.education")}
        >
          <ContentPanel>
            {educationLvl && (
              <Row style={{ alignItems: "baseline" }}>
                <Col>
                  <TitleInformation>{educationLvl}</TitleInformation>
                </Col>
              </Row>
            )}
            {courses && (
              <Row style={{ alignItems: "baseline" }}>
                {Boolean(degrees.length) && (
                  <Col span={12} style={{ marginBottom: 15 }}>
                    <TitleInformation>
                      {t(
                        "opportunity:process.candidateDetails.personalInfoTab.degrees"
                      )}
                    </TitleInformation>
                    <Tags
                      style={tagsStyle}
                      value={typeof degrees[0] === "string" ? degrees : []}
                    />
                  </Col>
                )}
                {Boolean(universities.length) && (
                  <Col span={12} style={{ marginBottom: 15 }}>
                    <TitleInformation>
                      {t(
                        "opportunity:process.candidateDetails.personalInfoTab.institute"
                      )}
                    </TitleInformation>
                    <Tags style={tagsStyle} value={universities} />
                  </Col>
                )}
              </Row>
            )}
            {fields && (
              <Row style={{ alignItems: "baseline" }}>
                {Boolean(fieldsOfStudy.length) && (
                  <Col span={12} style={{ marginBottom: 15 }}>
                    <TitleInformation>
                      {t(
                        "opportunity:process.candidateDetails.personalInfoTab.fieldsOfStudies"
                      )}
                    </TitleInformation>
                    <Tags
                      style={tagsStyle}
                      value={fieldsOfStudy.map(x => x[locale] || x.field)}
                    />
                  </Col>
                )}
                {Boolean(languages.length) && (
                  <Col span={12} style={{ marginBottom: 15 }}>
                    <TitleInformation>
                      {t("opportunity:process.candidateDetails.languages")}
                    </TitleInformation>
                    <Tags style={tagsStyle} value={languages} />
                  </Col>
                )}
              </Row>
            )}
          </ContentPanel>
        </Panel>
      )}
      {showProfessionalInfo && (
        <Panel
          header={
            <>
              <Title>
                {t("opportunity:process.candidateDetails.professionalExp")}
              </Title>
              {!R.isNil(experience) && (
                <>
                  <Icon
                    type="right"
                    style={{ marginLeft: 5, marginRight: 5, color: "#9ca8b1" }}
                  />
                  <DetailsItem>
                    {experience === 0 ? (
                      t(
                        "opportunity:process.candidateDetails.experience.noExperience"
                      )
                    ) : (
                      <>
                        {t(
                          `catalogs:experience.${experience
                            .toString()
                            .replace(".", ",")}`
                        )}
                        {t(
                          "opportunity:process.candidateDetails.experience.years"
                        )}
                      </>
                    )}
                  </DetailsItem>
                </>
              )}
            </>
          }
          key={t("opportunity:process.candidateDetails.professionalExp")}
        >
          <ContentPanel>
            <Row style={{ alignItems: "baseline" }}>
              {Boolean(professions.length) && (
                <Col span={12}>
                  <TitleInformation>
                    {t("opportunity:process.candidateDetails.expertiseArea")}
                  </TitleInformation>
                  <Tags
                    style={tagsStyle}
                    value={professions.map(x => x[locale] || x.label)}
                  />
                </Col>
              )}
              {Boolean(skills.length) && (
                <Col span={12}>
                  <TitleInformation>
                    {t("opportunity:process.candidateDetails.skills")}
                  </TitleInformation>
                  <Tags
                    style={tagsStyle}
                    value={skills.map(x => x[locale] || x.skill)}
                  />
                </Col>
              )}
            </Row>
          </ContentPanel>
        </Panel>
      )}
      {lastPositions && lastPositions.length > 0 && (
        <Panel
          header={
            <>
              <Title>
                {t("opportunity:process.candidateDetails.lastPositions")}
              </Title>
              <Icon
                type="right"
                style={{ marginLeft: 5, marginRight: 5, color: "#9ca8b1" }}
              />
              <DetailsItem>
                {currentPosition ? currentPosition.title : lastPosition.title}
              </DetailsItem>
            </>
          }
          key={t("opportunity:process.candidateDetails.lastPositions")}
        >
          <ContentPanel>
            {positionsSorted.map((position, index) => (
              <PositionItem key={index}>
                <Col span={12}>
                  <TitleInformation>
                    <b>{`${R.prop("title", position)} - ${R.prop(
                      "company",
                      position
                    )}`}</b>
                  </TitleInformation>
                  <ItemInformation>
                    {moment(R.prop("start", position)).format(
                      t(
                        "opportunity:process.candidateDetails.lastPositionFormat"
                      )
                    )}{" "}
                    -{" "}
                    {position.current
                      ? t("opportunity:process.candidateDetails.current")
                      : moment(R.prop("end", position)).format(
                          t(
                            "opportunity:process.candidateDetails.lastPositionFormat"
                          )
                        )}
                  </ItemInformation>
                </Col>
                {position.industries && Boolean(position.industries.length) && (
                  <Col span={12}>
                    <TitleInformation>
                      <b>{t("global:industries")}</b>
                    </TitleInformation>
                    <IndustriesContainer>
                      {position.industries.map(industry => (
                        <Tag style={tagsStyle}>{industry}</Tag>
                      ))}
                    </IndustriesContainer>
                  </Col>
                )}
              </PositionItem>
            ))}
          </ContentPanel>
        </Panel>
      )}
      {showTags && (
        <Panel
          header={
            <Title>{t("opportunity:process.candidateDetails.tags")}</Title>
          }
          key={t("opportunity:process.candidateDetails.tags")}
        >
          <ContentPanel>
            <CandidateTags
              candidates={[id]}
              opportunityId={opportunityId}
              selectedTags={selectedTags}
              allTags={companyTags}
              onSave={value => setSelectedTags(value.tags)}
            />
          </ContentPanel>
        </Panel>
      )}
    </Collapse>
  );
};

export default compose(
  withTranslation(["catalogs", "opportunity"]),
  connect((state, props) => {
    const { t } = props;
    const professions = FETCH_PROFESSIONS.getResponse(state) || [];
    const skills = FETCH_SKILLS.getResponse(state) || [];
    const studies = FETCH_STUDIES.getResponse(state) || [];

    return R.pipe(
      R.prop("candidate"),
      R.evolve({
        languages: R.map(
          ({ name, level }) =>
            `${getLanguage(name)} - ${t(`catalogs:languages.levels.${level}`)}`
        ),
        professions: R.pipe(
          R.map(profession => R.find(R.propEq("id", profession), professions)),
          R.filter(Boolean)
        ),
        skills: R.pipe(
          R.map(skill => R.find(R.propEq("skill", skill), skills)),
          R.filter(Boolean)
        ),
        fieldsOfStudy: R.pipe(
          R.map(study => R.find(R.propEq("field", study), studies)),
          R.filter(Boolean)
        )
      }),
      R.assoc("companyTags", R.pathOr([], ["company", "tags"], state).sort())
    )(props);
  })
)(CandidateDetails);
